/**
 * ============================================================================
 * API PROVIDER
 * ============================================================================
 * 
 * Provides API client with interceptors and error handling.
 * 401 refresh runs here (this interceptor is registered last = runs first).
 */

import React, { createContext, useContext, useEffect } from 'react';
import axios from 'axios';
import api, { API_BASE_URL, getStoredRefreshToken } from '../services/api';
import { useAuthStore } from '../store';
import { useUIStore } from '../store';
import { useOnlineStatus } from '../hooks/useOnlineStatus';

const APIContext = createContext(null);

let refreshPromise = null;
let isLoggingOut = false; // Flag to prevent new requests during logout
const pendingRequests = new Map(); // Track pending requests to cancel them

/**
 * API Provider Component
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
export const APIProvider = ({ children }) => {
  const { accessToken, logout } = useAuthStore();
  const { showError, showWarning, setGlobalLoading } = useUIStore();
  const isOnline = useOnlineStatus();

  // Helper function to handle logout and cleanup
  const handleLogout = () => {
    if (isLoggingOut) return; // Prevent multiple logout calls
    isLoggingOut = true;

    // Cancel all pending requests
    pendingRequests.forEach((cancel) => {
      try {
        cancel();
      } catch (e) {
        // Ignore cancellation errors
      }
    });
    pendingRequests.clear();

    clearWalletCache();

    // Clear tokens from localStorage
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
      } catch (e) {
        console.error('Error clearing localStorage:', e);
      }
    }

    // Logout from store
    logout();

    // Reset flag after a delay to allow redirect
    setTimeout(() => {
      isLoggingOut = false;
    }, 1000);
  };

  // Setup request interceptor
  useEffect(() => {
    const requestInterceptor = api.interceptors.request.use(
      (config) => {
        // Block requests if we're logging out
        if (isLoggingOut && !config.url?.includes('/auth/refresh') && !config.url?.includes('/auth/login')) {
          return Promise.reject(new axios.Cancel('Request cancelled: User is logging out'));
        }

        const isRefresh = config.url?.includes('/auth/refresh');
        if (!isRefresh) {
          const token = accessToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('accessToken') : null);
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }

        // Track request for cancellation
        const cancelToken = axios.CancelToken.source();
        config.cancelToken = cancelToken.token;
        const requestId = config.url || Math.random().toString();
        pendingRequests.set(requestId, () => cancelToken.cancel('Request cancelled: Session expired'));

        // Show global loading for long requests
        if (config.showGlobalLoading !== false) {
          setGlobalLoading(true, config.loadingMessage);
        }

        return config;
      },
      (error) => {
        setGlobalLoading(false);
        return Promise.reject(error);
      }
    );

    // Setup response interceptor (runs FIRST due to LIFO; handle 401 refresh here)
    const responseInterceptor = api.interceptors.response.use(
      (response) => {
        setGlobalLoading(false);
        return response;
      },
      async (error) => {
        setGlobalLoading(false);

        // Ignore cancelled requests
        if (axios.isCancel(error)) {
          return Promise.reject(error);
        }

        const originalRequest = error.config;
        const isRefreshRequest = originalRequest?.url?.includes('/auth/refresh');
        const status = error.response?.status;

        // Remove from pending requests
        if (originalRequest?.url) {
          pendingRequests.delete(originalRequest.url);
        }

        // Debug logging
        if (status === 401) {
          console.log('[APIProvider] 401 detected:', {
            url: originalRequest?.url,
            isRefreshRequest,
            _retry: originalRequest?._retry,
            hasRefreshToken: !!getStoredRefreshToken(),
          });
        }

        // 401 on protected request: try refresh + retry (we run first, so we must do it here)
        if (status === 401 && !isRefreshRequest && !originalRequest._retry && !isLoggingOut) {
          console.log('[APIProvider] Entering 401 refresh branch');
          originalRequest._retry = true;
          const token = getStoredRefreshToken();
          console.log('[APIProvider] Refresh token found:', !!token);
          if (!token) {
            console.log('[APIProvider] No refresh token, logging out');
            handleLogout();
            showError('Session expired. Please login again.');
            if (typeof window !== 'undefined') {
              // Use replace instead of href to prevent back button issues
              window.location.replace('/login');
            }
            return Promise.reject(error);
          }
          try {
            console.log('[APIProvider] Calling refresh endpoint...');
            if (!refreshPromise) {
              refreshPromise = axios
                .post(`${API_BASE_URL}/auth/refresh`, { refreshToken: token })
                .then((res) => {
                  console.log('[APIProvider] Refresh response received:', res.status);
                  const tokens = res.data?.data?.tokens || res.data?.tokens;
                  if (!tokens?.accessToken) throw new Error('Invalid refresh response');
                  return tokens;
                })
                .catch((err) => {
                  // If refresh fails with 401, the refresh token is expired
                  if (err.response?.status === 401) {
                    console.log('[APIProvider] Refresh token expired');
                    handleLogout();
                    showError('Session expired. Please login again.');
                    if (typeof window !== 'undefined') {
                      window.location.replace('/login');
                    }
                  }
                  throw err;
                })
                .finally(() => {
                  refreshPromise = null;
                });
            }
            const tokens = await refreshPromise;
            
            // Check if we're logging out (refresh might have failed)
            if (isLoggingOut) {
              return Promise.reject(new Error('Session expired'));
            }
            
            console.log('[APIProvider] Refresh successful, updating tokens and retrying');
            if (typeof localStorage !== 'undefined') {
              localStorage.setItem('accessToken', tokens.accessToken);
              if (tokens.refreshToken) localStorage.setItem('refreshToken', tokens.refreshToken);
            }
            try {
              useAuthStore.getState().setTokens({
                accessToken: tokens.accessToken,
                refreshToken: tokens.refreshToken ?? (typeof localStorage !== 'undefined' ? localStorage.getItem('refreshToken') : null),
              });
            } catch (_) {}
            originalRequest.headers.Authorization = `Bearer ${tokens.accessToken}`;
            // Remove old request from pending and add new one
            if (originalRequest.url) {
              pendingRequests.delete(originalRequest.url);
            }
            return api(originalRequest);
          } catch (refreshErr) {
            console.error('[APIProvider] Refresh failed:', refreshErr);
            // Only logout if not already logging out
            if (!isLoggingOut) {
              handleLogout();
              showError('Session expired. Please login again.');
              if (typeof window !== 'undefined') {
                window.location.replace('/login');
              }
            }
            return Promise.reject(refreshErr);
          }
        }

        // 401 on refresh request: session expired
        if (error.response?.status === 401 && isRefreshRequest) {
          handleLogout();
          showError('Session expired. Please login again.');
          if (typeof window !== 'undefined') {
            window.location.replace('/login');
          }
          return Promise.reject(error);
        }

        // Handle network errors
        if (!error.response) {
          if (!isOnline) {
            showWarning('You are offline. Please check your connection.');
          } else {
            showError('Network error. Please try again.');
          }
          return Promise.reject(error);
        }

        // Handle other errors (backend uses data.error.message or data.message)
        const data = error.response?.data;
        const message =
          data?.error?.message ||
          data?.message ||
          error.message ||
          'An error occurred';

        switch (status) {
          case 400:
            showError(message || 'Invalid request');
            break;
          case 403:
            showError('You do not have permission to perform this action');
            break;
          case 404:
            showError('Resource not found');
            break;
          case 429:
            showWarning('Too many requests. Please try again later.');
            break;
          case 500:
          case 502:
          case 503:
            showError('Server error. Please try again later.');
            break;
          default:
            showError(message);
        }

        return Promise.reject(error);
      }
    );

    // Cleanup
    return () => {
      api.interceptors.request.eject(requestInterceptor);
      api.interceptors.response.eject(responseInterceptor);
      // Cancel all pending requests on unmount
      pendingRequests.forEach((cancel) => {
        try {
          cancel();
        } catch (e) {
          // Ignore cancellation errors
        }
      });
      pendingRequests.clear();
    };
  }, [accessToken, logout, showError, showWarning, setGlobalLoading, isOnline]);

  const value = {
    api,
    isOnline,
  };

  return (
    <APIContext.Provider value={value}>
      {children}
    </APIContext.Provider>
  );
};

/**
 * Use API context
 * @returns {Object} API context value
 */
export const useAPIContext = () => {
  const context = useContext(APIContext);
  if (!context) {
    // Fallback to direct import
    return {
      api: require('../services/api').default,
      isOnline: true,
    };
  }
  return context;
};

export default APIProvider;
